import { defineNuxtRouteMiddleware, useRuntimeConfig } from '#imports'

import { useRedirectLocale } from '../composables/useRedirectLocale'
import { useUserStore } from '../composables/useUserStore'
import { findRouteOrParentMatchingPredicate } from '../utils/routes'

/**
 * The guest middleware.
 *
 * It makes sure that people are guest (non-authenticated).
 *
 * This middleware is globally available as soon as you register the OAuth
 * module in your application. You do not have to do anything to start
 * filtering page views.
 *
 * In order to prevent access to a page to authenticated people, you have
 * to provide some route meta configuration using the `meta.guest` property.
 *
 * Only the `required` value is mandatory. If set to `true`, access will be
 * prevented to authenticated people. If set to `false`, then everyone will
 * be able to access the page (both guest and authenticated users).
 *
 * @example
 * const route = {
 *   name: 'login',
 *   path: '/login',
 *   component: () => import('./components/Login'),
 *   meta: {
 *     guest: {
 *       required: true,
 *     },
 *   },
 * }
 *
 * If the access is prevented, the user will be redirected to the redirect
 * location configured when the module was instantiated. This is configured
 * via the `defaultRedirectLocationWhenAuthenticated` module option.
 *
 * However, it is possible to define a custom redirection location for a
 * specific route. This is achieved via the `meta.guest.redirection` property.
 *
 * @example
 * const route = {
 *   name: 'login',
 *   path: '/login',
 *   component: () => import('./components/Login'),
 *   meta: {
 *     auth: {
 *       required: true,
 *       redirection: { name: 'home' }
 *     },
 *   },
 * }
 */
export default defineNuxtRouteMiddleware(async (route) => {
  const config = useRuntimeConfig().public.oauth
  const { isUserFetched, isUserAuthenticated, fetchUser } = useUserStore()

  const matchedRouteWithGuest = findRouteOrParentMatchingPredicate(
    route,
    (routeDefinition) => !!(routeDefinition.meta && routeDefinition.meta.guest),
  )

  // We may request a routes tree not protected at all.
  if (matchedRouteWithGuest === undefined) return
  if (matchedRouteWithGuest.meta.guest === undefined) return
  if (matchedRouteWithGuest.meta.guest.required === false) return

  // We don't want to fire the request each time the middleware is instanciated.
  // If the authentication status was previously fetched already, there is no
  // need to fetch it again, and we should use the data saved in our store.
  if (!isUserFetched.value) {
    await fetchUser()
  }

  // We're good to go, we're a guest.
  if (!isUserAuthenticated.value) return

  const { getRouteLocationWithLocale } = useRedirectLocale()

  // We are expected to return different values based on the expected behaviour.
  // eslint-disable-next-line consistent-return
  return (
    matchedRouteWithGuest.meta.guest.redirection ||
    getRouteLocationWithLocale(config.defaultRedirectLocationWhenAuthenticated)
  )
})
